// ============================================
// B356 Fase 1 (AI_154) — COFRE DE NUMERAÇÃO DO SNCR (Firestore)
// ============================================
// Um documento por (médico, ambiente, tipo) em `numeracoes-sncr`:
//   { uid, ambiente, tipo, blocos: BlocoNumeracao[], usados, criadoEm, atualizadoEm }
// e, em `numeracoes-sncr/{id}/usos/{numero}`, o registro append-only de cada
// número consumido (rastreabilidade — RDC 1.000/2025, Art. 5º IV).
//
// Consumo ATÔMICO (transação): lê `usados`, calcula o próximo número a partir
// dos blocos, grava `usados + 1` e o registro de uso. Duas abas assinando ao
// mesmo tempo não pegam o mesmo número: o Firestore refaz a transação perdedora.
// As rules (firestore.rules, bloco numeracoes-sncr) só aceitam `usados` subir de
// 1 em 1 com os blocos intactos, ou um bloco NOVO acrescentado com `usados`
// intacto — nem o próprio médico consegue "voltar" o contador.

import {
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../lib/firebase";
import { AmbienteSncr } from "./configSncr";
import {
  BlocoNumeracao,
  TipoNumeracao,
  numeroNaPosicao,
  saldo,
  totalNumeros,
  validarBloco,
} from "./numeracaoSncr";

export const COLECAO_NUMERACOES = "numeracoes-sncr";

export const idCofre = (uid: string, amb: AmbienteSncr, tipo: TipoNumeracao) =>
  `${uid}_${amb}_${tipo}`;

export interface EstadoCofre {
  tipo: TipoNumeracao;
  blocos: BlocoNumeracao[];
  usados: number;
  saldo: number;
  total: number;
}

export class SemSaldoSncr extends Error {
  constructor(tipo: TipoNumeracao) {
    super(
      `Sem numeração ${tipo} do SNCR disponível. Obtenha um novo bloco em hepato.app.br/sncr.`
    );
  }
}

export async function lerCofre(
  uid: string,
  amb: AmbienteSncr,
  tipo: TipoNumeracao
): Promise<EstadoCofre> {
  const snap = await getDoc(doc(db, COLECAO_NUMERACOES, idCofre(uid, amb, tipo)));
  const blocos: BlocoNumeracao[] = snap.exists() ? snap.data().blocos || [] : [];
  const usados: number = snap.exists() ? snap.data().usados || 0 : 0;
  return {
    tipo,
    blocos,
    usados,
    saldo: saldo(blocos, usados),
    total: totalNumeros(blocos),
  };
}

/** Guarda um bloco recém-obtido no SNCR (depois de validar a coerência). */
export async function registrarBloco(
  uid: string,
  amb: AmbienteSncr,
  tipo: TipoNumeracao,
  bloco: BlocoNumeracao
): Promise<void> {
  const erro = validarBloco(bloco);
  if (erro) throw new Error(`Bloco do SNCR recusado: ${erro}`);
  const ref = doc(db, COLECAO_NUMERACOES, idCofre(uid, amb, tipo));
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      tx.set(ref, {
        uid,
        ambiente: amb,
        tipo,
        blocos: [bloco],
        usados: 0,
        criadoEm: serverTimestamp(),
        atualizadoEm: serverTimestamp(),
      });
      return;
    }
    const blocos: BlocoNumeracao[] = snap.data().blocos || [];
    if (blocos.some((b) => b.inicio === bloco.inicio)) return; // idempotente
    tx.update(ref, {
      blocos: [...blocos, bloco],
      atualizadoEm: serverTimestamp(),
    });
  });
}

/**
 * Consome o PRÓXIMO número (transação). `referencia` descreve o documento que
 * recebeu o número (ex.: id da prescrição) e fica no registro de uso.
 */
export async function consumirNumero(
  uid: string,
  amb: AmbienteSncr,
  tipo: TipoNumeracao,
  referencia: Record<string, string>
): Promise<string> {
  const ref = doc(db, COLECAO_NUMERACOES, idCofre(uid, amb, tipo));
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new SemSaldoSncr(tipo);
    const blocos: BlocoNumeracao[] = snap.data().blocos || [];
    const usados: number = snap.data().usados || 0;
    const numero = numeroNaPosicao(blocos, usados);
    if (!numero) throw new SemSaldoSncr(tipo);
    tx.update(ref, { usados: usados + 1, atualizadoEm: serverTimestamp() });
    tx.set(doc(ref, "usos", numero), {
      uid,
      numero,
      tipo,
      ambiente: amb,
      ...referencia,
      usadoEm: serverTimestamp(),
    });
    return numero;
  });
}
